import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';

import { MpsUserService } from '../../mps/services/mps-user.service';
import { applicationRouteNames } from '../application-route-names';
import { MpsUser } from '../models/mps-user.model';

/**
 * Generic role gate (spec 042) — allows navigation iff the current user's role code is one of
 * `roleCodes`, else redirects to home. Mirrors the shape of `app-courrier-agent.guard.ts`.
 */
export const appRoleGuard = (...roleCodes: string[]): CanActivateFn => {
  return () => {
    const router = inject(Router);
    const userService = inject(MpsUserService);
    const loggedInUser = userService.getUser();

    if (!loggedInUser) {
      return router.navigateByUrl(applicationRouteNames.home);
    }

    return loggedInUser.role?.code && roleCodes.includes(loggedInUser.role.code)
      ? true
      : router.navigateByUrl(applicationRouteNames.home);
  };
};

/**
 * Roles-permissions "edit" per-tab gate (spec 042) — allows navigation iff the current user can
 * manage the role carried on `route.data.roleCode` (see `MpsUser.canManageRole`), else redirects
 * to home. COURRIER_AGENT never has any manageable target; ADMIN_METIER is blocked on ADMIN_IT.
 */
export const appManageRoleGuard: CanActivateFn = (route) => {
  const router = inject(Router);
  const userService = inject(MpsUserService);
  const loggedInUser = userService.getUser();

  if (!loggedInUser) {
    return router.navigateByUrl(applicationRouteNames.home);
  }

  const targetRoleCode = route.data['roleCode'] as string | undefined;
  const user = new MpsUser(loggedInUser);

  return targetRoleCode && user.canManageRole(targetRoleCode) ? true : router.navigateByUrl(applicationRouteNames.home);
};
